import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Loader2, Share } from "lucide-react";
import { Button } from "@/components/ui/button";
import IosInstallSheet from "@/components/IosInstallSheet";
import { isAndroidDevice, isIOSDevice, trackEvent } from "@/lib/platform";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";


type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

/** Official Tennis Log listing on Google Play. */
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.tennislog.app";

type PwaWindow = Window & {
  __deferredInstallPrompt?: BeforeInstallPromptEvent | null;
  __appInstalled?: boolean;
  __pwaInstallPromptInProgress?: boolean;
  __pwaInstallDiagnostics?: Record<string, unknown>;
  __logPwaInstall?: (label: string, details?: unknown) => void;
  __setPwaInstallState?: (patch: Record<string, unknown>, eventName?: string) => void;
};

interface Props {
  variant?: "header" | "footer";
  className?: string;
}

export default function InstallAppButton({ variant = "header", className = "" }: Props) {
  const { t } = useTranslation();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    () => (typeof window !== "undefined" ? ((window as PwaWindow).__deferredInstallPrompt ?? null) : null),
  );
  const [installed, setInstalled] = useState(
    () => (typeof window !== "undefined" ? !!(window as PwaWindow).__appInstalled : false),
  );
  const [prompting, setPrompting] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [iosSheetOpen, setIosSheetOpen] = useState(false);
  const [unavailableOpen, setUnavailableOpen] = useState(false);

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  // Any iPhone/iPad, including Chrome / in-app browsers — they all need the
  // iOS-specific guidance rather than an Android-style install prompt.
  const isIOS = isIOSDevice();
  const isAndroid = isAndroidDevice() && !isIOS;


  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      // @ts-ignore iOS Safari
      window.navigator.standalone === true);

  // Capability check: browsers that will never fire beforeinstallprompt.
  // iOS Safari doesn't expose the event but supports Add-to-Home-Screen manually.
  // Firefox / desktop Safari expose neither the event nor manual installation.
  const supportsBeforeInstallPrompt =
    typeof window !== "undefined" && "onbeforeinstallprompt" in window;

  const installMode = useMemo(() => {
    if (isIOS) return "ios";
    if (isAndroid) return "android";
    return "desktop";
  }, [isAndroid, isIOS]);

  useEffect(() => {
    const win = window as PwaWindow;
    const log = (label: string, details?: unknown) => win.__logPwaInstall?.(label, details);

    if (isStandalone) setInstalled(true);
    const cached = win.__deferredInstallPrompt ?? null;
    if (cached) setDeferred(cached);
    if (win.__appInstalled) setInstalled(true);

    const onAvailable = () => {
      const ev = win.__deferredInstallPrompt ?? null;
      if (ev) setDeferred(ev);
    };
    const onBefore = (e: Event) => {
      const ev = e as BeforeInstallPromptEvent;
      e.preventDefault();
      win.__deferredInstallPrompt = ev;
      setDeferred(ev);
      log("beforeinstallprompt received");
    };
    const onInstalled = () => {
      win.__appInstalled = true;
      win.__deferredInstallPrompt = null;
      setInstalled(true);
      setDeferred(null);
    };

    window.addEventListener("pwa-install-available", onAvailable);
    window.addEventListener("pwa-installed", onInstalled);
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("pwa-install-available", onAvailable);
      window.removeEventListener("pwa-installed", onInstalled);
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [isStandalone]);

  if (installed) return null;

  // Never keep the button in a loading state. The label is always the CTA
  // (or "Opening…" while the native prompt is actually opening). Detection
  // of what to do happens on click, so users don't stare at "loading".
  const label = prompting
    ? t("install.opening", { defaultValue: "Opening install prompt…" })
    : isIOS
      ? t("install.iosCta", { defaultValue: "Install on iPhone" })
      : t("install.cta");

  const handleClick = async () => {
    const win = window as PwaWindow;

    // iOS never fires beforeinstallprompt and has no App Store build:
    // always show the Add-to-Home-Screen sheet (or the "open in Safari" note).
    if (isIOS) {
      trackEvent("install_cta_clicked", { platform: "ios", destination: "ios_sheet", source: variant });
      trackEvent("ios_install_help_opened", { source: variant });
      setIosSheetOpen(true);
      return;
    }

    // Android phones/tablets (incl. in-app webviews): Tennis Log is on
    // Google Play. Never send an installed/standalone app back to the store;
    // otherwise go straight to the Play listing — Android/Chrome hands the
    // URL to the Play app when available, web listing otherwise.
    if (isAndroid && !isStandalone) {
      trackEvent("install_cta_clicked", { platform: "android", destination: "play_store", source: variant });
      window.location.assign(PLAY_STORE_URL);
      return;
    }

    const promptEvent = deferred ?? win.__deferredInstallPrompt ?? null;


    if (promptEvent) {
      trackEvent("install_cta_clicked", { platform: installMode, destination: "pwa_prompt", source: variant });
      setPrompting(true);
      win.__pwaInstallPromptInProgress = true;
      try {
        await promptEvent.prompt();
        const choice = await promptEvent.userChoice;
        win.__logPwaInstall?.("native install prompt choice", choice.outcome);
        if (choice.outcome === "accepted") {
          win.__appInstalled = true;
          setInstalled(true);
        }
      } catch (error) {
        win.__logPwaInstall?.("native install prompt failed", String(error));
      } finally {
        win.__deferredInstallPrompt = null;
        win.__pwaInstallPromptInProgress = false;
        setDeferred(null);
        setPrompting(false);
      }
      return;
    }

    // No native prompt available. Pick the correct fallback immediately.
    // Desktop: show manual instructions (browser menu install).
    // Android without prompt (or unsupported browsers): show unavailable.
    if ((!isAndroid && !supportsBeforeInstallPrompt) || installMode === "desktop") {
      trackEvent("install_cta_clicked", { platform: installMode, destination: "manual_help", source: variant });
      setHelpOpen(true);
      return;
    }


    setUnavailableOpen(true);
  };

  const unavailableDialog = (
    <Dialog open={unavailableOpen} onOpenChange={setUnavailableOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-primary" /> {t("install.title")}
          </DialogTitle>
          <DialogDescription>
            {t("install.unavailable", {
              defaultValue: "Install option is not available in this browser.",
            })}
          </DialogDescription>
        </DialogHeader>
        {isAndroid && (
          <ol className="text-sm space-y-2 list-decimal pl-5">
            <li>{t("install.androidStep1")}</li>
            <li>{t("install.androidStep2")}</li>
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );

  const helpDialog = (
    <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5 text-primary" /> {t("install.title")}
          </DialogTitle>
          <DialogDescription>{t("install.hint")}</DialogDescription>
        </DialogHeader>
        {installMode === "ios" ? (
          <ol className="text-sm space-y-2 list-decimal pl-5">
            <li>
              {t("install.iosStep1")} <Share className="inline w-4 h-4 align-text-bottom" />
            </li>
            <li>{t("install.iosStep2")}</li>
          </ol>
        ) : installMode === "android" ? (
          <ol className="text-sm space-y-2 list-decimal pl-5">
            <li>{t("install.androidStep1")}</li>
            <li>{t("install.androidStep2")}</li>
          </ol>
        ) : (
          <div className="text-sm space-y-2">
            <p>{t("install.desktopHint")}</p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>{t("install.desktopStep1")}</li>
              <li>{t("install.desktopStep2")}</li>
            </ol>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  if (variant === "footer") {
    return (
      <>
        <button
          onClick={handleClick}
          disabled={prompting}
          aria-label={label}
          className={`hover:text-foreground transition-colors flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
        >
          {prompting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} {label}
        </button>
        {helpDialog}
        {unavailableDialog}
        <IosInstallSheet open={iosSheetOpen} onOpenChange={setIosSheetOpen} source={variant} />
      </>
    );
  }

  return (
    <>
      <Button
        size="sm"
        onClick={handleClick}
        disabled={prompting}
        aria-label={label}
        title={label}
        className={`h-8 px-2 sm:px-3 text-xs bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-glow disabled:opacity-70 ${className}`}
      >
        {prompting ? <Loader2 className="w-3.5 h-3.5 sm:mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 sm:mr-1" />}
        <span className="hidden sm:inline max-w-44 truncate">{label}</span>
      </Button>
      {helpDialog}
      {unavailableDialog}
      <IosInstallSheet open={iosSheetOpen} onOpenChange={setIosSheetOpen} source={variant} />
    </>
  );
}
